/**
 * Local storage for trips a traveller has explicitly saved for offline use.
 *
 * IndexedDB rather than localStorage because a saved trip is a whole itinerary —
 * days, activities, costs, member items — and that runs to hundreds of kilobytes
 * on a long trip, well past what localStorage will hold alongside everything
 * else the app keeps there.
 *
 * Every record is keyed by user as well as trip. Phones get shared and handed
 * around, and a snapshot contains hotel addresses and confirmation numbers, so
 * signing in as someone else must not surface the previous person's trips.
 */

const DB_NAME = 'dejavu-offline';
const DB_VERSION = 1;
const STORE = 'trips';
const USER_INDEX = 'by_user';

// Bumped whenever the snapshot shape changes. An older snapshot is discarded
// rather than migrated: the trip is one fetch away whenever there is a network,
// so guessing at a migration risks showing wrong details on a travel day.
export const SNAPSHOT_VERSION = 1;

const snapshotKey = (userId, tripId) => `${userId}:${tripId}`;

export function isOfflineStorageAvailable() {
  try {
    return typeof indexedDB !== 'undefined' && indexedDB !== null;
  } catch {
    return false;
  }
}

function openDatabase() {
  return new Promise((resolve, reject) => {
    if (!isOfflineStorageAvailable()) {
      reject(new Error('IndexedDB is not available'));
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: 'key' });
        store.createIndex(USER_INDEX, 'userId', { unique: false });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error('Could not open the offline database'));
    request.onblocked = () => reject(new Error('The offline database is blocked by another tab'));
  });
}

/**
 * Runs one transaction and closes the connection.
 *
 * Holding a connection open across a session would block the upgrade path for
 * any other tab, and these operations are rare enough — a save, a read on load
 * — that the cost of reopening does not matter.
 */
async function withStore(mode, work) {
  const db = await openDatabase();

  try {
    return await new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE, mode);
      const store = transaction.objectStore(STORE);
      let result;

      const request = work(store);
      if (request) {
        request.onsuccess = () => {
          result = request.result;
        };
      }

      transaction.oncomplete = () => resolve(result);
      transaction.onerror = () => reject(transaction.error || new Error('Offline storage transaction failed'));
      transaction.onabort = () => reject(transaction.error || new Error('Offline storage transaction aborted'));
    });
  } finally {
    db.close();
  }
}

/**
 * Writes the snapshot, replacing any previous save of the same trip.
 *
 * `savedAt` is what the UI shows the traveller, and it is the only honest
 * measure of how stale what they are looking at might be.
 */
export async function saveTripSnapshot({ userId, tripId, trip, itinerary = [], memberItinerary = [] }) {
  if (!userId || !tripId) {
    throw new Error('A snapshot needs both a user and a trip');
  }

  const snapshot = {
    key: snapshotKey(userId, tripId),
    userId: String(userId),
    tripId: String(tripId),
    version: SNAPSHOT_VERSION,
    savedAt: new Date().toISOString(),
    trip,
    itinerary,
    memberItinerary
  };

  await withStore('readwrite', (store) => store.put(snapshot));
  return snapshot;
}

/**
 * Reads a snapshot, or null when there is none this user can use.
 *
 * A snapshot written by an older version of the app is deleted on read instead
 * of returned, so a stale shape cannot reach the itinerary renderer.
 */
export async function getTripSnapshot(userId, tripId) {
  if (!userId || !tripId) return null;

  const snapshot = await withStore('readonly', (store) => store.get(snapshotKey(userId, tripId)));
  if (!snapshot) return null;

  if (snapshot.version !== SNAPSHOT_VERSION) {
    await deleteTripSnapshot(userId, tripId);
    return null;
  }

  return snapshot;
}

export async function deleteTripSnapshot(userId, tripId) {
  if (!userId || !tripId) return;
  await withStore('readwrite', (store) => store.delete(snapshotKey(userId, tripId)));
}

export async function listTripSnapshots(userId) {
  if (!userId) return [];

  const snapshots = await withStore('readonly', (store) =>
    store.index(USER_INDEX).getAll(String(userId))
  );

  return (snapshots || [])
    .filter((snapshot) => snapshot.version === SNAPSHOT_VERSION)
    .sort((a, b) => (a.savedAt < b.savedAt ? 1 : -1));
}

/**
 * Drops every saved trip. Called on sign-out: the next person to use the device
 * should not find someone else's hotel addresses waiting for them.
 */
export async function clearAllTripSnapshots() {
  if (!isOfflineStorageAvailable()) return;
  await withStore('readwrite', (store) => store.clear());
}
